import { useEffect, useRef } from "react";
import { useAuthStore } from "@src/features/auth/auth-store";
import { useFeatureFlags, useBooleanFlag, FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

/**
 * Force-logs-out a session still on the legacy Django flow once the
 * `force_logout_legacy_auth` flag is enabled. Used to migrate remaining
 * Django sessions during the Auth0 cutover; native only (web never has a
 * legacy session, see useLegacyAuth).
 */
export const useForceLogout = (): void => {
  const { isInitialized } = useFeatureFlags();
  const shouldForceLogout = useBooleanFlag(FEATURE_FLAGS.FORCE_LOGOUT_LEGACY_AUTH);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const legacyAuth = useAuthStore((state) => state.legacyAuth);
  const accessToken = useAuthStore((state) => state.accessToken);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const hasForcedLogout = useRef(false);

  useEffect(() => {
    if (!isInitialized || hasForcedLogout.current || !shouldForceLogout) return;

    // Same "on legacy flow" check as useAuth0Upsell: a legacy token with no
    // Auth0 token means this session is authenticated via Django.
    const isOnLegacyFlow = isAuthenticated && legacyAuth !== null && accessToken === null;
    if (!isOnLegacyFlow) return;

    hasForcedLogout.current = true;
    void clearAuth().then(() => {
      // Reset so a future legacy session (e.g. the user logs back into Django
      // while the flag is still on) gets force-logged-out too, rather than
      // this firing at most once per app session.
      hasForcedLogout.current = false;
    });
  }, [isInitialized, shouldForceLogout, isAuthenticated, legacyAuth, accessToken, clearAuth]);
};
